// Verträge des DB-Portals, Phase C: Lieferungen (Upload), Dokumente, Auswertung mit Fundstellen,
// Freigabe durch GQL und Übertragung nach JurNodes. Schema-Kennungen `db-portal/…/1`.
import { z } from 'zod';
// entgeltaenderung: Mitteilung über geänderte (meist erhöhte) Entgelte – keine Rechnung; hakt den Sammelpunkt
// „Entgeltänderungen seit Vertragsbeginn“ ab (erwünscht, seit 08.10.2026), ebenso „agb“ den Punkt „AGB / Vertragsbedingungen“
/** agb = Allgemeine Geschäfts- bzw. (Weitere) Vertragsbedingungen als eigenes Dokument (im Vertragsformular enthaltene zählen zum Vertrag) */
export const dbPortalDokumentArt = z.enum(['vertrag', 'rechnung', 'leistungsbeschreibung', 'agb', 'entgeltaenderung', 'sonstiges']);
/** wartet → in_arbeit → erkannt | fehler */
export const dbPortalDokumentStatus = z.enum(['wartet', 'in_arbeit', 'erkannt', 'fehler']);
export const dbPortalDokumentHerkunft = z.enum(['datei', 'zip', 'kamera']);
/** offen → freigegeben | abgelehnt | nachforderung (Nachforderung: Nutzer liefert in neuer Lieferung nach) */
export const dbPortalLieferungStatus = z.enum(['offen', 'freigegeben', 'abgelehnt', 'nachforderung']);
/** aus = Übertragung abgeschaltet; trocken = nur Plan erstellt; uebertragen = in JurNodes abgelegt */
export const dbPortalJnStatus = z.enum(['aus', 'wartet', 'trocken', 'uebertragen', 'fehler']);
/** Wer hat ausgewertet: feste Regeln (deterministisch) oder KI (Bedrock EU). */
export const dbPortalAuswertungsQuelle = z.enum(['regeln', 'ki']);
/** Fundstelle im Dokumenttext; `belegt` = Zitat wurde im Text tatsächlich gefunden (deterministisch geprüft). */
export const dbPortalFundstelle = z.object({
    seite: z.number().int().positive().nullable(),
    zitat: z.string().max(300),
    belegt: z.boolean(),
});
export const dbPortalFeld = z.object({
    wert: z.string().max(300).nullable(),
    fundstelle: dbPortalFundstelle.nullable(),
});
export const dbPortalZeitraum = z.enum(['monat', 'quartal', 'jahr', 'einmalig', 'unbekannt']);
export const dbPortalEntgelt = z.object({
    bezeichnung: z.string().max(200),
    betragText: z.string().max(40),
    /** aus `betragText` deterministisch berechnet; null, wenn nicht eindeutig lesbar */
    betragCent: z.number().int().nullable(),
    zeitraum: dbPortalZeitraum,
    steuer: z.enum(['netto', 'brutto', 'unbekannt']),
    fundstelle: dbPortalFundstelle.nullable(),
});
export const dbPortalPosition = z.object({
    bezeichnung: z.string().max(200),
    menge: z.string().max(40).nullable(),
    einzelpreisText: z.string().max(40).nullable(),
    gesamtText: z.string().max(40).nullable(),
    gesamtCent: z.number().int().nullable(),
    fundstelle: dbPortalFundstelle.nullable(),
});
/** Betrag mit Wortlaut, deterministisch berechneten Cent und Fundstelle */
export const dbPortalBetragFeld = z.object({
    text: z.string().max(40).nullable(),
    cent: z.number().int().nullable(),
    fundstelle: dbPortalFundstelle.nullable(),
});
/**
 * Entgeltänderung (z. B. Bosch „Änderung/Anpassung der Entgelte“): monatliches Entgelt netto vorher und nachher.
 * `erhoehung` aus dem Schreiben, sonst neu − bisher; `prozent` und `stimmig` (bisher + Erhöhung = neu) berechnet.
 */
export const dbPortalEntgeltaenderung = z.object({
    gueltigAb: dbPortalFeld,
    bisher: dbPortalBetragFeld,
    neu: dbPortalBetragFeld,
    erhoehung: dbPortalBetragFeld,
    prozent: z.number().nullable(),
    stimmig: z.boolean(),
    /** Bezugszeitraum der Beträge laut Schreiben (ältere Bosch-Schreiben: Quartalsentgelt); fehlt = monatlich */
    zeitraum: z.enum(['monat', 'quartal', 'jahr']).optional(),
    /** im Schreiben genannte Erhöhungssätze im Wortlaut (z. B. je Vertragsart) */
    saetze: dbPortalFeld,
    /** Anteil „Sicherheits-/Zeitsysteme“ (Bosch-Leistung ohne durchgereichte Leitungsgebühren) – erste Spalte der Tabelle, falls ausgewiesen */
    anteil: z
        .object({ bisher: dbPortalBetragFeld, neu: dbPortalBetragFeld, erhoehung: dbPortalBetragFeld, prozent: z.number().nullable() })
        .nullable()
        .optional(),
});
/** Art einer Komponente – deterministisch aus der Bezeichnung abgeleitet, damit Preise über Standorte vergleichbar sind */
export const dbPortalKomponentenArt = z.enum([
    'aufschaltung',
    'leitung',
    'uebertragungsgeraet',
    'handfeuermelder',
    'schliesszylinder',
    'router',
    'meldekriterium',
    'einsatzdatei',
    'erweiterungskarte',
    'leitstellenkriterien',
    'stromversorgung',
    'antenne',
    'konzessionsabgabe',
    'dienstleistung',
    'sonstiges',
]);
/** Zeile einer Komponenten-Preistabelle (Bosch-Rechnungen und Vertragsunterlagen: „Menge | Miete monatlich | Einrichtung“) */
export const dbPortalKomponente = z.object({
    bezeichnung: z.string().max(200),
    art: dbPortalKomponentenArt,
    /** Typbezeichnung des Herstellers, z. B. „FMC-120-DKM-H-R“ */
    typ: z.string().max(40).nullable(),
    menge: z.number().int().positive().nullable(),
    mengeText: z.string().max(20).nullable(),
    /** Miete monatlich netto für die ganze Zeile (alle Stück) */
    mieteText: z.string().max(40).nullable(),
    mieteCent: z.number().int().nullable(),
    /** Miete monatlich je Stück: ausdrücklich genannt oder (`einzelpreisBerechnet`) Zeilenbetrag ÷ Menge */
    einzelpreisText: z.string().max(40).nullable(),
    einzelpreisCent: z.number().int().nullable(),
    einzelpreisBerechnet: z.boolean(),
    /** einmaliges Einrichtungsentgelt netto */
    einrichtungText: z.string().max(40).nullable(),
    einrichtungCent: z.number().int().nullable(),
    /** eigener Preis | im Paketpreis enthalten (Zeilen mit Preis ergeben die Tabellensumme) | Preis nicht lesbar */
    preis: z.enum(['eigener', 'im_paket', 'nicht_lesbar']),
    /** bei `im_paket`: Bezeichnung der vorangehenden Zeile mit Preis */
    paket: z.string().max(200).nullable(),
    fundstelle: dbPortalFundstelle.nullable(),
});
export const dbPortalKomponentenTabelle = z.object({
    zeilen: z.array(dbPortalKomponente).max(60),
    /** Tabellensumme Miete monatlich laut Dokument (Zwischensumme bzw. „Gesamte monatliche Miete“) */
    summe: dbPortalBetragFeld,
    summeEinrichtung: dbPortalBetragFeld,
    /** Summe der Zeilen (Miete monatlich), berechnet */
    summeBerechnetCent: z.number().int().nullable(),
    /** Zeilen ergeben die Tabellensumme(n); null, wenn keine Summe lesbar ist */
    stimmig: z.boolean().nullable(),
});
export const dbPortalExtraktion = z.object({
    schema: z.literal('db-portal/extraktion/1'),
    quelle: dbPortalAuswertungsQuelle,
    /** Inferenzprofil bei KI-Auswertung, sonst null */
    modell: z.string().nullable(),
    art: dbPortalDokumentArt,
    artSicherheit: z.enum(['hoch', 'mittel', 'niedrig']),
    jahr: z.number().int().min(1990).max(2100).nullable(),
    /** ISO-Datum (JJJJ-MM-TT) in `wert` */
    dokumentDatum: dbPortalFeld,
    konzessionaer: dbPortalFeld.extend({ slug: z.string().nullable() }),
    vertragsnummer: dbPortalFeld,
    rechnungsnummer: dbPortalFeld,
    serviceklasse: dbPortalFeld,
    laufzeit: z.object({
        beginn: z.string().max(10).nullable(),
        ende: z.string().max(10).nullable(),
        text: z.string().max(300).nullable(),
        fundstelle: dbPortalFundstelle.nullable(),
    }),
    standortHinweis: dbPortalFeld,
    entgelte: z.array(dbPortalEntgelt).max(50),
    positionen: z.array(dbPortalPosition).max(100),
    /** nur bei Entgeltänderungen (ältere Auswertungen ohne dieses Feld) */
    entgeltaenderung: dbPortalEntgeltaenderung.nullable().optional(),
    /** Komponenten-Preistabellen (ältere Auswertungen ohne dieses Feld) */
    komponenten: z.array(dbPortalKomponentenTabelle).max(6).optional(),
    hinweise: z.array(z.string().max(300)).max(20),
    /** Text länger als die Auswertungsgrenze: nur der Anfang wurde ausgewertet (wird angezeigt, nie still) */
    textGekuerzt: z.boolean(),
});
export const dbPortalDokument = z.object({
    id: z.string().uuid(),
    lieferungId: z.string().uuid(),
    standortId: z.string().uuid(),
    dateiname: z.string(),
    mime: z.string(),
    groesse: z.number().int().nonnegative(),
    herkunft: dbPortalDokumentHerkunft,
    zipPfad: z.string().nullable(),
    status: dbPortalDokumentStatus,
    /** verständliche Meldung für den Nutzer (ohne technische Details) */
    fehler: z.string().nullable(),
    ocr: z.boolean().nullable(),
    seiten: z.number().int().nullable(),
    artVorgabe: dbPortalDokumentArt.nullable(),
    jahrVorgabe: z.number().int().nullable(),
    extraktion: dbPortalExtraktion.nullable(),
    /** automatisch abgehakter Checklistenpunkt */
    checkliste: z
        .object({
        art: z.enum(['vertrag', 'rechnung', 'leistungsbeschreibung', 'agb', 'entgeltaenderung']), jahr: z.number().int()
    }).nullable(),
    angelegtAm: z.string(),
    verarbeitetAm: z.string().nullable(),
});
export const dbPortalLieferung = z.object({
    id: z.string().uuid(),
    standortId: z.string().uuid(),
    standortName: z.string(),
    status: dbPortalLieferungStatus,
    kommentar: z.string().nullable(),
    /** Text der Entscheidung von GQL (z. B. Nachforderung), für den Nutzer sichtbar */
    entscheidungText: z.string().nullable(),
    entschiedenAm: z.string().nullable(),
    jnStatus: dbPortalJnStatus,
    angelegtAm: z.string(),
    dokumente: z.array(dbPortalDokument),
});
export const dbPortalLieferungListe = z.object({
    lieferungen: z.array(dbPortalLieferung),
    /** true, solange noch Dokumente ausgewertet werden (Oberfläche fragt dann erneut ab) */
    inArbeit: z.boolean(),
});
export const dbPortalUploadErgebnis = z.object({
    lieferungen: z.array(z.object({ id: z.string().uuid(), standortId: z.string().uuid(), dokumente: z.number().int() })),
    angenommen: z.array(z.object({ id: z.string().uuid(), dateiname: z.string(), herkunft: dbPortalDokumentHerkunft, zipPfad: z.string().nullable() })),
    abgewiesen: z.array(z.object({ dateiname: z.string(), zipPfad: z.string().nullable(), grund: z.string() })),
});
export const dbPortalEntscheidung = z.object({
    entscheidung: z.enum(['freigeben', 'ablehnen', 'nachfordern']),
    text: z.string().trim().max(2000).optional(),
});
/** Textbausteine für Nachforderungen (Admin wählt aus und passt an) */
export const DB_PORTAL_TEXTBAUSTEINE = {
    unleserlich: 'Einige Seiten sind nicht lesbar. Bitte laden Sie die Unterlagen erneut in besserer Qualität hoch (Scan oder PDF).',
    jahr_fehlt: 'Für den Kernzeitraum fehlt noch die Rechnung für das Jahr {jahr}. Bitte laden Sie diese nach oder vermerken Sie, dass sie nicht vorliegt.',
    vertrag_fehlt: 'Bitte laden Sie den Vertrag mit dem Konzessionär (einschließlich Anlagen und Preisblatt) hoch.',
    falscher_standort: 'Die Unterlagen gehören offenbar zu einem anderen Standort. Bitte prüfen Sie die Zuordnung und laden Sie sie beim richtigen Standort hoch.',
};
//# sourceMappingURL=db-portal-lieferung.js.map